import React from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Lozenge from '@atlaskit/lozenge';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type RenderVariant,
	RenderVariantSwitcherDropdown,
	SegmentationTargetFieldV2,
	SegmentationTargetGroupsField,
	SegmentationTargets,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { type Card } from '../../../../types';

import { messages } from './messages';

const styles = cssMap({
	wrapper: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		paddingBottom: token('space.200'),
	},
	wrapperWithoutBorder: {
		paddingBottom: token('space.200'),
	},
	marginBottom: {
		marginBottom: token('space.050'),
	},
});

export type CardsConfigContentSectionMainProps = {
	audienceSegmentationGroupIds?: string[];
	cards: Card[];
	extensionActions: CardsActions;
	renderVariant: RenderVariant;
	segmentationTarget?: SegmentationTargets;
	source: string;
	updateRenderVariant: (renderVariant: RenderVariant) => void;
};

export const CardsConfigContentSectionMain = ({
	segmentationTarget,
	audienceSegmentationGroupIds,
	renderVariant,
	extensionActions,
	updateRenderVariant,
	cards,
	source,
}: CardsConfigContentSectionMainProps): React.JSX.Element => {
	const { updateMainFormField, updateCardFormFieldForAllCards } = extensionActions;
	const { enableTargeting, enableUnifiedSearchFeatures, enableSmartCardsFields, isCompanyHub } =
		useProductDependencies();

	// isCompanyHub is false and enableTargeting is true for Non company hub such as confluence page
	const isTargetingEnabled = isCompanyHub && enableTargeting;

	const shouldShowSegmentationTargetGroupsField =
		segmentationTarget === SegmentationTargets.ALL && isTargetingEnabled;

	const handleGroupSelection = (selectedGroups: string[] | undefined) => {
		updateMainFormField({ audienceSegmentationGroupIds: selectedGroups });
	};

	return (
		<Stack
			xcss={enableSmartCardsFields ? styles.wrapper : styles.wrapperWithoutBorder}
			space="space.200"
			testId="cards-config-content-section-main"
		>
			{enableUnifiedSearchFeatures && (
				<RenderVariantSwitcherDropdown
					renderVariant={renderVariant}
					onUpdate={updateRenderVariant}
					source={source}
				/>
			)}

			{isTargetingEnabled && (
				<Box xcss={styles.marginBottom}>
					<Lozenge appearance="new">
						<FormattedMessage {...messages.newLozengeLabel} />
					</Lozenge>
					<SegmentationTargetFieldV2
						audienceSegmentationGroupIds={audienceSegmentationGroupIds}
						updateMainFormField={updateMainFormField}
						targetParameter={segmentationTarget}
						updateCardFormFieldForAllCards={updateCardFormFieldForAllCards}
						renderVariant={renderVariant}
						source={source}
					/>
				</Box>
			)}
			{shouldShowSegmentationTargetGroupsField && (
				<SegmentationTargetGroupsField
					targetGroupsIds={
						segmentationTarget === SegmentationTargets.ALL
							? audienceSegmentationGroupIds
							: cards[0]?.audienceSegmentationGroupIds
					}
					onGroupSelected={handleGroupSelection}
					showUpdatedStyles
					source={source}
				/>
			)}
		</Stack>
	);
};
